import { useEffect, useState } from "react";
import api from "@/lib/api";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, CartesianGrid } from "recharts";
import { useTr } from "@/i18n/translator";

export default function Analytics() {
  const tr = useTr();
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/analytics").then(({ data }) => setData(data)); }, []);

  if (!data) return <div className="font-mono-mark text-xs text-[#5C5C5C]">CARGANDO…</div>;

  return (
    <div className="space-y-6" data-testid="analytics-page">
      <div>
        <div className="font-mono-mark text-[10px] text-[#5C5C5C]">{tr("INTELIGENCIA · MÉTRICAS")}</div>
        <h1 className="font-display text-4xl sm:text-5xl mt-2">Analytics unificados</h1>
      </div>

      {/* Totals */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-0 border border-[#0A0A0A]">
        <KPI label="SEGUIDORES" value={data.totals.followers.toLocaleString()} />
        <KPI label="IMPRESIONES" value={data.totals.impressions.toLocaleString()} border />
        <KPI label="ENGAGEMENT" value={data.totals.engagement_rate + "%"} border />
        <KPI label="POSTS PUBLICADOS" value={data.totals.posts_published} border />
      </div>

      {/* Timeline */}
      <div className="bg-white border border-[#0A0A0A] p-6">
        <div className="font-mono-mark text-[10px] text-[#5C5C5C] mb-4">{tr("TELEMETRÍA · 14 DÍAS")}</div>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data.timeline}>
              <CartesianGrid stroke="#E5E5E5" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }} tickFormatter={(d) => d.slice(5)} stroke="#0A0A0A" />
              <YAxis tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }} stroke="#0A0A0A" />
              <Tooltip contentStyle={{ background: "#0A0A0A", color: "white", border: "none", borderRadius: 0, fontFamily: "JetBrains Mono", fontSize: 12 }} />
              <Line type="monotone" dataKey="impressions" stroke="#0A0A0A" strokeWidth={2} dot={{ r: 2, fill: "#E53935" }} />
              <Line type="monotone" dataKey="engagements" stroke="#E53935" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* By network */}
        <div className="bg-white border border-[#0A0A0A] p-6">
          <div className="font-mono-mark text-[10px] text-[#5C5C5C] mb-4">{tr("POR RED · IMPRESIONES")}</div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.by_network}>
                <CartesianGrid stroke="#E5E5E5" vertical={false} />
                <XAxis dataKey="network" tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }} stroke="#0A0A0A" />
                <YAxis tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }} stroke="#0A0A0A" />
                <Tooltip contentStyle={{ background: "#0A0A0A", color: "white", border: "none", fontFamily: "JetBrains Mono", fontSize: 12 }} />
                <Bar dataKey="impressions" fill="#0A0A0A" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Best hours */}
        <div className="bg-white border border-[#0A0A0A] p-6">
          <div className="font-mono-mark text-[10px] text-[#5C5C5C] mb-4">MEJORES HORAS · SCORE</div>
          <ul className="divide-y divide-[#0A0A0A]">
            {data.best_hours.sort((a, b) => b.score - a.score).map((h) => (
              <li key={h.hour} className="py-3 flex items-center gap-4">
                <span className="font-display text-2xl w-20">{h.hour}</span>
                <div className="flex-1 h-6 bg-[#E5E5E5] relative">
                  <div className="absolute top-0 left-0 bottom-0 bg-[#E53935]" style={{ width: `${h.score}%` }} />
                </div>
                <span className="font-mono-data text-sm w-14 text-right">{h.score}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Network table */}
      <div className="bg-white border border-[#0A0A0A] overflow-hidden">
        <div className="px-6 py-4 border-b border-[#0A0A0A] font-mono-mark text-[10px] text-[#5C5C5C]">{tr("DETALLE POR RED")}</div>
        <table className="w-full text-sm font-mono-data">
          <thead className="bg-[#0A0A0A] text-white">
            <tr>
              <th className="text-left px-4 py-3 font-mono-mark text-[10px]">RED</th>
              <th className="text-right px-4 py-3 font-mono-mark text-[10px]">{tr("SEGUIDORES")}</th>
              <th className="text-right px-4 py-3 font-mono-mark text-[10px]">{tr("IMPRESIONES")}</th>
              <th className="text-right px-4 py-3 font-mono-mark text-[10px]">{tr("ENGAGEMENT")}</th>
              <th className="text-right px-4 py-3 font-mono-mark text-[10px]">{tr("POSTS")}</th>
            </tr>
          </thead>
          <tbody>
            {data.by_network.map((n) => (
              <tr key={n.network} className="border-t border-[#0A0A0A]">
                <td className="px-4 py-3 capitalize">{n.network}</td>
                <td className="text-right px-4 py-3">{n.followers.toLocaleString()}</td>
                <td className="text-right px-4 py-3">{n.impressions.toLocaleString()}</td>
                <td className="text-right px-4 py-3">{n.engagement_rate}%</td>
                <td className="text-right px-4 py-3">{n.posts}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function KPI({ label, value, border }) {
  return (
    <div className={`p-6 bg-white ${border ? "border-l border-[#0A0A0A]" : ""}`}>
      <div className="font-mono-mark text-[10px] text-[#5C5C5C]">{label}</div>
      <div className="font-display text-4xl mt-3 font-mono-data">{value}</div>
    </div>
  );
}
